import { useEffect } from "react"
import { EndSessionDialog } from "../trading/EndSessionDialog.tsx"
import { ReplayControls } from "../trading/ReplayControls.tsx"
import { syncChartPreference, useAccounts, useAccountSummary, useSyncChart } from "../trading/tradingData.ts"
import { useEndSession } from "../trading/useEndSession.ts"
import { ReplayStartDialog } from "./ReplayStartDialog.tsx"
import { replaySession, useReplaySession } from "./replaySession.ts"

/** The followed account, when the charts follow its replay (docs/design/0007). */
const useReplayingAccount = () => {
  const accounts = useAccounts()
  const live = useAccountSummary()
  const sync = useSyncChart()
  const account = live ?? accounts.accounts.find((candidate) => candidate.id === accounts.selectedId) ?? null
  return sync && account !== null && account.status !== "Archived" && account.clock._tag === "Replay" ? account : null
}

/**
 * "Replay" in the top toolbar (docs/design/0007): pick a starting bar (Escape
 * cancels); the dialog then picks the paper account. While the charts follow a
 * replay the button is pressed, and picking again starts from another bar.
 */
export function BarReplayBar() {
  const session = useReplaySession()
  const replaying = useReplayingAccount() !== null

  // Escape cancels picking a bar.
  useEffect(() => {
    if (session.status !== "picking") return
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && replaySession.done()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [session.status])

  return (
    <>
      {session.status === "picking"
        ? (
          <span className="bar-replay" role="group" aria-label="Picking a bar">
            <span className="bar-replay-hint">Click a bar on a chart</span>
            <button type="button" className="link" onClick={() => replaySession.done()}>Cancel</button>
          </span>
        )
        : (
          <button
            type="button"
            className="toolbar-button"
            aria-label="Replay"
            aria-pressed={replaying}
            title={replaying
              ? "Start the replay from another bar"
              : "Replay: pick a bar, then play the chart forward from it on a paper account; the backtest and paper trading follow the same clock"}
            onClick={() => replaySession.pick()}
          >
            ⏮ Replay
          </button>
        )}
      {session.status === "choosing" && <ReplayStartDialog picked={session.picked} />}
    </>
  )
}

/**
 * The replay's controls, in a strip under the toolbar while the charts follow a
 * Replay account: its clock (play, pause, step, speed, time), the account, ending
 * its session (docs/design/0008), and leaving the replay view. Backtest, chart,
 * orders and strategy runners all follow it.
 */
export function ReplayStrip() {
  const account = useReplayingAccount()
  const endSession = useEndSession(account)
  if (account === null) return null
  return (
    <div className="replay-strip" role="group" aria-label="Replay">
      <span className="replay-strip-title">Replay</span>
      <ReplayControls account={account} compact />
      <span className="bar-replay-account" title="The paper account this replay trades on">{account.name}</span>
      <span className="toolbar-spacer" />
      <button type="button" className="link" title="Start the replay from another bar" onClick={() => replaySession.pick()}>
        Another bar
      </button>
      <button
        type="button"
        className="small"
        title="Finish this session and see how it went: open orders are cancelled, strategies stopped, and the account becomes read-only"
        onClick={endSession.ask}
      >
        End session
      </button>
      <button
        type="button"
        title="Exit replay view: the charts show every bar again (the session keeps going; End session finishes it)"
        aria-label="Exit replay view"
        onClick={() => syncChartPreference.set("off")}
      >
        ✕
      </button>
      {endSession.confirming && <EndSessionDialog account={account} end={endSession} />}
    </div>
  )
}
